import { BasicToolkitMode } from "./toolbar";
import { ExportFormat } from "../../utils/images/imageConvertFormats";
import { ContextProps } from "./contextMenu";
import { DrawingCursorPreviewProps, DrawingStrokePreviewProps, MarqueeSelectionPreviewProps, SelectionPreviewProps, ShapePreviewProps } from "./layerComponent";
import { LayerProps, ExtendPadding, OverlayRect, LayerAreaSelection, OpenExtandImageState } from "./images";

export type OverlaySide = "top" | "right" | "bottom" | "left";
export type OverlayCorner = "nw" | "ne" | "sw" | "se";

const sideNormals: Record<OverlaySide, { x: number; y: number }> = {
    top: { x: 0, y: -1 },
    right: { x: 1, y: 0 },
    bottom: { x: 0, y: 1 },
    left: { x: -1, y: 0 }
};

const oppositeSide = (side: OverlaySide): OverlaySide => {
    if (side === "top") return "bottom";
    if (side === "bottom") return "top";
    if (side === "left") return "right";
    return "left";
};

export const getTransformedSideNormal = (
    layer: Pick<LayerProps, "rotation" | "flipHorizontal" | "flipVertical"> | null,
    localSide: OverlaySide
) => {
    let { x, y } = sideNormals[localSide];

    if (layer?.flipHorizontal) x *= -1;
    if (layer?.flipVertical) y *= -1;

    const radians = ((layer?.rotation ?? 0) * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    return {
        x: x * cos - y * sin,
        y: x * sin + y * cos
    };
};

export const getVisualToLocalSideMap = (
    layer: Pick<LayerProps, "rotation" | "flipHorizontal" | "flipVertical"> | null
): Record<OverlaySide, OverlaySide> => {
    const localSides: OverlaySide[] = ["top", "right", "bottom", "left"];
    const visualSides: OverlaySide[] = ["top", "right", "bottom", "left"];

    const used = new Set<OverlaySide>();
    const result = {} as Record<OverlaySide, OverlaySide>;

    for (const visualSide of visualSides) {
        const visualNormal = sideNormals[visualSide];

        let bestLocal: OverlaySide = "top";
        let bestScore = -Infinity;

        for (const localSide of localSides) {
            if (used.has(localSide)) continue;

            const normal = getTransformedSideNormal(layer, localSide);
            const score = normal.x * visualNormal.x + normal.y * visualNormal.y;

            if (score > bestScore) {
                bestScore = score;
                bestLocal = localSide;
            }
        }

        result[visualSide] = bestLocal;
        used.add(bestLocal);
    }

    return result;
};

export const getLocalToVisualSideMap = (
    layer: Pick<LayerProps, "rotation" | "flipHorizontal" | "flipVertical"> | null
): Record<OverlaySide, OverlaySide> => {
    const visualToLocal = getVisualToLocalSideMap(layer);
    const result = {} as Record<OverlaySide, OverlaySide>;

    (Object.keys(visualToLocal) as OverlaySide[]).forEach((visualSide) => {
        result[visualToLocal[visualSide]] = visualSide;
    });

    return result;
};

export const getLocalCornerForVisualCorner = (
    layer: Pick<LayerProps, "rotation" | "flipHorizontal" | "flipVertical"> | null,
    corner: OverlayCorner
): OverlayCorner => {
    const map = getVisualToLocalSideMap(layer);

    const visualY: OverlaySide = corner.includes("n") ? "top" : "bottom";
    const visualX: OverlaySide = corner.includes("w") ? "left" : "right";

    const localY = map[visualY];
    const localX = map[visualX];

    const yPart = localY === "top" ? "n" : localY === "bottom" ? "s" : "";
    const xPart = localX === "left" ? "w" : localX === "right" ? "e" : "";

    if (yPart && xPart) return `${yPart}${xPart}` as OverlayCorner;

    // fallback for 90° cases where top maps to left/right and left maps to top/bottom
    const localSides = [localY, localX];
    const nOrS = localSides.includes("top") ? "n" : "s";
    const wOrE = localSides.includes("left") ? "w" : "e";

    return `${nOrS}${wOrE}` as OverlayCorner;
};

export const getOutwardDragAmountPx = (
    ev: MouseEvent,
    startX: number,
    startY: number,
    layer: Pick<LayerProps, "rotation" | "flipHorizontal" | "flipVertical"> | null,
    visualSide: OverlaySide
) => {
    const map = getVisualToLocalSideMap(layer);
    const localSide = map[visualSide];
    const normal = getTransformedSideNormal(layer, localSide);

    const len = Math.hypot(normal.x, normal.y) || 1;
    const unit = {
        x: normal.x / len,
        y: normal.y / len
    };

    return (
        (ev.clientX - startX) * unit.x +
        (ev.clientY - startY) * unit.y
    );
};

export const visualPaddingToLocalPadding = (
    layer: Pick<LayerProps, "rotation" | "flipHorizontal" | "flipVertical">,
    padding: ExtendPadding
): ExtendPadding => {
    const map = getVisualToLocalSideMap(layer);

    const result: ExtendPadding = {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
    };

    result[map.top] = padding.top;
    result[map.right] = padding.right;
    result[map.bottom] = padding.bottom;
    result[map.left] = padding.left;

    return result;
};

export type ColorPickTarget =
    | 'transparencyColor'
    | 'transparencyTransitionColor'
    | 'drawingColor'
    | 'textColor'
    | 'backgroundColorText'
    | 'borderColorText'
    | 'backgroundColorImage'
    | 'borderColorImage';
export interface ActiveOverlayBaseRectCropProps {
    left: number;
    top: number;
    width: number;
    height: number;
    fullCanvasLeftInWrapper: number;
    fullCanvasTopInWrapper: number;
    fullCanvasDisplayWidth: any;
    fullCanvasDisplayHeight: any;
};
export interface ActiveOverlayLayerDisplayRectProps {
    left: number;
    top: number;
    width: number;
    height: number;
};
export interface DragStateRefProps {
    mode: null | 'move' | 'nw' | 'ne' | 'sw' | 'se';
    startX: number;
    startY: number;
    startRect: OverlayRect;
};

export interface DraggingFunctionsProps {
   overlayRect:  OverlayRect;
   activeOverlayBaseRect: ActiveOverlayBaseRectCropProps | ActiveOverlayLayerDisplayRectProps |null;
   setOverlayRect: (overlayRect: OverlayRect) => void;
};

export interface OverlayPortalPropsImageStudio {
    canvasRef: any;
    canvasWrapperRef: React.RefObject<HTMLDivElement>;
    canvasDisplayRect: {
        left: number;
        top: number;
        width: number;
        height: number;
    } | null;
    overlayRect: OverlayRect;
    activeOverlayBaseRect: ActiveOverlayBaseRectCropProps | ActiveOverlayLayerDisplayRectProps |null;
    overlayTarget: "image" | "layer";
    activeToolkitTool?: BasicToolkitMode | null;
    setOverlayRect: (overlayRect: OverlayRect) => void;
    closeOverlays: () => void;
    setContextMenu: React.Dispatch<React.SetStateAction<ContextProps>>;
    drawingCursorPreview?: DrawingCursorPreviewProps | null;
    imageLayer: LayerProps[];
    selectedLayer?: LayerProps | null;
    selectedLayerIDs?: number[];
    activeLayerEditId: number | null;
    layerAreaSelection: LayerAreaSelection | null;
    selectionPreview: SelectionPreviewProps | null;
    marqueeSelectionPreview: MarqueeSelectionPreviewProps | null;
    shapePreview?: ShapePreviewProps | null;
    drawingStrokePreview?: DrawingStrokePreviewProps | null;
    updatePointerSelection: (e: React.MouseEvent, layer: LayerProps) => void;
    beginLayerTransform: (e: React.MouseEvent,layer: LayerProps,mode: "move" | "nw" | "ne" | "sw" | "se") => void;
    beginSelectionDrag: (e: React.MouseEvent, targetLayer?: LayerProps | null) => void;
    beginDrawingStroke: (e: React.MouseEvent) => void;
    onSelectedLayerChange?: (layer: LayerProps | null) => void;
    onSelectedLayerIDsChange?: (ids: number[]) => void;
    beginPointerMarqueeSelection: (e: React.MouseEvent) => void;
    setLayerAreaSelection: React.Dispatch<React.SetStateAction<LayerAreaSelection | null>>;
    selectedDrawingTool?: string;
    cropMode: boolean;
    rotateMode: boolean;
    rotationDraft?: number;
    isPickingColor?: boolean;
    onPickSurfaceColor?: (e: React.MouseEvent<HTMLElement>) => void;
    openRotate: (initial: number,layerId?: number) => void;
    onIsRotating?: (isRotating: boolean) => void;
    handleCropSave: () => void;
    handleRotateSave: () => void;
    editingTextLayerId?: number | null;
    editingTextValue?: string;
    onStartTextEdit?: (layer: LayerProps) => void;
    onChangeEditingText?: (text: string) => void;
    onCommitTextEdit?: () => void;
    //dragging from outside on canvas
    onDropImageOnSelectedImageLayer?: (file: File) => Promise<void>;
    onDropImageOnCanvas?: (file: File, x: number, y: number) => Promise<void>;
    onResolveGalleryImageFile?: (thumbnailName: string) => Promise<File | null>;
    //dragging from image gallery on canvas
    onDropGalleryImageOnCanvas?: (
        thumbnailName: string,
        x: number,
        y: number
    ) => Promise<void>;

onDropGalleryImageOnSelectedImageLayer?: (
    thumbnailName: string
) => Promise<void>;
    //expand image
    openExtandImage: OpenExtandImageState;
    onChangeExtendPadding: (padding: ExtendPadding,anchor?: { clientX: number; clientY: number }) => void;
    onCancelExtendImage: () => void;
    onSaveExtendImage: () => void;
};
